/**
 * CO Save for Later / Wishlist Module
 *
 * Stores saved items in localStorage. Can be upgraded to sync with
 * customer metafields via Storefront API.
 *
 * Storage format: Array of objects with variant info
 * [{ variantId, productId, productTitle, variantTitle, price, image, url, savedAt }]
 */

const CO_WISHLIST_KEY = 'co_saved_items';

class COSaveForLater {
  constructor() {
    this.items = this.load();
    this.listeners = new Set();
  }

  /**
   * Load saved items from localStorage
   * @returns {Array}
   */
  load() {
    try {
      const stored = localStorage.getItem(CO_WISHLIST_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('[CO SaveForLater] Error loading:', e);
      return [];
    }
  }

  /**
   * Save items to localStorage
   */
  save() {
    try {
      localStorage.setItem(CO_WISHLIST_KEY, JSON.stringify(this.items));
      this.notifyListeners();
    } catch (e) {
      console.error('[CO SaveForLater] Error saving:', e);
    }
  }

  /**
   * Add an item to saved items
   * @param {Object} item - Item data
   * @param {number|string} item.variantId - Variant ID
   * @param {number|string} item.productId - Product ID
   * @param {string} item.productTitle - Product title
   * @param {string} item.variantTitle - Variant title (e.g., "Natural / 0-3M")
   * @param {number} item.price - Price in cents
   * @param {string} item.image - Image URL
   * @param {string} item.url - Product URL
   * @returns {boolean} - True if added, false if already exists
   */
  add(item) {
    const variantId = String(item.variantId);

    /* Check if already saved */
    if (this.has(variantId)) {
      return false;
    }

    this.items.push({
      variantId,
      productId: String(item.productId),
      productTitle: item.productTitle,
      variantTitle: item.variantTitle || '',
      price: item.price,
      compareAtPrice: item.compareAtPrice || null,
      image: item.image,
      url: item.url,
      savedAt: new Date().toISOString()
    });

    this.save();
    this.dispatchEvent('add', { variantId, item: this.items[this.items.length - 1] });
    return true;
  }

  /**
   * Remove an item from saved items
   * @param {number|string} variantId - Variant ID to remove
   * @returns {boolean} - True if removed, false if not found
   */
  remove(variantId) {
    const id = String(variantId);
    const index = this.items.findIndex(item => item.variantId === id);

    if (index === -1) {
      return false;
    }

    const removed = this.items.splice(index, 1)[0];
    this.save();
    this.dispatchEvent('remove', { variantId: id, item: removed });
    return true;
  }

  /**
   * Toggle an item (add if not saved, remove if saved)
   * @param {Object} item - Item data (same as add())
   * @returns {boolean} - True if now saved, false if removed
   */
  toggle(item) {
    const variantId = String(item.variantId);

    if (this.has(variantId)) {
      this.remove(variantId);
      return false;
    } else {
      this.add(item);
      return true;
    }
  }

  /**
   * Check if a variant is saved
   * @param {number|string} variantId - Variant ID to check
   * @returns {boolean}
   */
  has(variantId) {
    const id = String(variantId);
    return this.items.some(item => item.variantId === id);
  }

  /**
   * Get all saved items
   * @returns {Array}
   */
  getAll() {
    return [...this.items];
  }

  /**
   * Get count of saved items
   * @returns {number}
   */
  getCount() {
    return this.items.length;
  }

  /**
   * Clear all saved items
   */
  clear() {
    this.items = [];
    this.save();
    this.dispatchEvent('clear', {});
  }

  /**
   * Move item to cart (remove from saved, add to cart)
   * @param {number|string} variantId - Variant ID
   * @param {number} quantity - Quantity to add (default 1)
   * @returns {Promise}
   */
  async moveToCart(variantId, quantity = 1) {
    const id = String(variantId);
    const item = this.items.find(i => i.variantId === id);

    if (!item) {
      throw new Error('Item not found in saved items');
    }

    /* Add to cart via Shopify AJAX API */
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    /* Remove from saved items */
    this.remove(variantId);

    /* Dispatch cart update event for theme to handle */
    document.dispatchEvent(new CustomEvent('co:cart:add', {
      detail: { variantId, quantity }
    }));

    return response.json();
  }

  /**
   * Save item from cart (remove from cart, add to saved)
   * @param {Object} cartItem - Cart line item object
   * @returns {Promise}
   */
  async saveFromCart(cartItem) {
    /* Add to saved items */
    this.add({
      variantId: cartItem.variant_id || cartItem.id,
      productId: cartItem.product_id,
      productTitle: cartItem.product_title,
      variantTitle: cartItem.variant_title,
      price: cartItem.price,
      compareAtPrice: cartItem.compare_at_price,
      image: cartItem.image || cartItem.featured_image?.url,
      url: cartItem.url
    });

    /* Remove from cart via Shopify AJAX API */
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: String(cartItem.key || cartItem.variant_id || cartItem.id),
        quantity: 0
      })
    });

    if (!response.ok) {
      /* Rollback: remove from saved items */
      this.remove(cartItem.variant_id || cartItem.id);
      const error = await response.json();
      throw new Error(error.description || 'Failed to remove from cart');
    }

    /* Dispatch cart update event for theme to handle */
    document.dispatchEvent(new CustomEvent('co:cart:update', {
      detail: { action: 'save-for-later' }
    }));

    return response.json();
  }

  /**
   * Subscribe to changes
   * @param {Function} callback - Called with (event, data)
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback('change', { items: this.items, count: this.items.length });
      } catch (e) {
        console.error('[CO SaveForLater] Listener error:', e);
      }
    });
  }

  /**
   * Dispatch a custom event
   * @param {string} eventName - Event name (add, remove, clear)
   * @param {Object} detail - Event detail
   */
  dispatchEvent(eventName, detail) {
    document.dispatchEvent(new CustomEvent(`co:wishlist:${eventName}`, {
      detail: { ...detail, count: this.items.length }
    }));
  }
}

/* Create singleton instance */
window.COSaveForLater = window.COSaveForLater || new COSaveForLater();

/* Export for module usage */
export { COSaveForLater };
export default window.COSaveForLater;
